// @flow

import React, { Component } from 'react';
import ReactDOM from 'react-dom';
import { connect } from 'react-redux';
import ErrorBoundary from 'app/packages/error-boundary';
import StandardMasonry from 'app/common/react/components/Masonry9/StandardMasonry';
import type { Dispatch } from 'redux';
import type { Item } from 'app/common/react/components/Masonry/types';
import { Masonry } from 'gestalt';
import { SingleTreatmentExp } from 'app/common/lib/experiment';
import { type Action } from 'app/common/redux/actions/types';
import { type CurrentHistoryEntry } from 'app/common/redux/reducers/history';
import { type HistoryLocation } from 'app/common/redux/actions/history';
import { type State as Pins } from 'app/common/redux/reducers/pins';
import { type State as ReduxState } from 'app/common/redux/types';
import { useRequestContext } from 'app/common/react/components/RequestContext';
import ExperimentalMasonry, {
  type MasonryPaddingStyle,
  type MasonryType,
} from 'app/common/react/ui/experimental/ExperimentalMasonry';
import {
  setRoutePins as setRoutePinsAction,
  getPinIdsFromItems,
} from 'app/common/redux/actions/routePins';
import {
  startTiming,
  stopTiming,
  logCustomTraceEvent,
} from 'app/common/lib/TraceLogger/logTraceEvent';

type ReduxStateProps = {|
  currentHistoryEntry: ?CurrentHistoryEntry,
  itemsNormalizedTracing: ?(Item[]),
|};

type ReduxDispatchProps = {|
  setRoutePins: (pinIds: Array<string>, location: HistoryLocation) => Object,
|};

type OwnProps = {|
  // MasonryPerfTiming Props:
  cacheKey?: ?string, // key for item measurement cache
  error?: Error,
  isFetching: boolean,
  mapPinUrlsToItems?: (state: Pins, items: Item[]) => Item[],
  saveRoutePins?: boolean,

  // TODO (btraut): using any to see if the PCONST stuff is causing bundle size increase
  // viewParameterType: $Values<PCONST.thrift.ViewParameterType>,
  viewParameterType: any,

  // Masonry Props:
  columnWidth?: number,
  comp: Function,
  fetchBoundsOffset?: number,
  flexible?: boolean,
  gutterWidth?: number,
  items: Array<Object>,
  loadItems: () => void,
  masonryRef?: { current: ?MasonryType },
  paddingStyle?: MasonryPaddingStyle,
  scrollContainer?: () => HTMLElement,
  serverRender?: boolean,
  useMasonry9?: boolean,
  virtualize?: boolean,
  widthOffset?: number,
  isGridCentered?: boolean,
  minCols?: number,
|};

type ConnectedProps = {|
  ...ReduxStateProps,
  ...ReduxDispatchProps,
  ...OwnProps,
|};

type Props = {|
  ...ReduxStateProps,
  ...ReduxDispatchProps,
  ...OwnProps,
|};

const MEASUREMENT_CACHE: {
  [key: string]: {
    screenWidth: number,
    cache: Object,
  },
} = {};

const getMeasurementCache = (cacheKey: ?string, useMasonry9: ?boolean) => {
  if (cacheKey == null || __SERVER__) {
    return undefined;
  }

  // Masonry blindly accepts measurement stores even if the screen has changed
  // size, so we need to be sure to clear it here when requesting the same
  // measurements for a different screen.
  if (
    !MEASUREMENT_CACHE[cacheKey] ||
    MEASUREMENT_CACHE[cacheKey].screenWidth !== window.innerWidth
  ) {
    MEASUREMENT_CACHE[cacheKey] = {
      screenWidth: window.innerWidth,
      cache: useMasonry9
        ? StandardMasonry.createMeasurementStore()
        : Masonry.createMeasurementStore(),
    };
  }

  return MEASUREMENT_CACHE[cacheKey].cache;
};

const getWindow = typeof window !== 'undefined' ? () => window : undefined;

class MasonryPerfTiming extends Component<Props> {
  static defaultProps = {
    saveRoutePins: false,
    serverRender: true,
    useMasonry9: false,
    isGridCentered: true,
  };

  constructor(props: Props) {
    super(props);
    logCustomTraceEvent({ name: 'MasonryConstructor' });
  }

  componentDidMount() {
    const { isFetching, saveRoutePins } = this.props;

    // Check if the viewport will be filled by the first page of pins
    // If not, then wait for the next page of pins to load and check again
    // using the bounding box instead of the viewport since the user may have
    // scrolled at this point
    const screenHeight = window.innerHeight;
    // Grab the first pin by querying for the first image that has a srcset property
    const firstPin = document.querySelector('img[srcset]');
    if (firstPin) {
      const topOffset = firstPin.getBoundingClientRect() && firstPin.getBoundingClientRect().top;
      // The height of the grid visible in the viewport
      this.gridVisibleHeight = screenHeight && screenHeight - topOffset;
    }

    logCustomTraceEvent({ name: 'MasonryDidMount' });
    logCustomTraceEvent({
      name: 'PageLoadRegisterGridImagesTraceEvent',
      viewportFilled: this.checkViewportFilled(),
      gridData: this.props.itemsNormalizedTracing || this.props.items,
      el: ReactDOM.findDOMNode(this),
    });

    if (!isFetching) {
      logCustomTraceEvent({
        name: 'UserNavRegisterGridImagesTraceEvent',
        viewportFilled: this.checkViewportFilled(),
        gridData: this.props.itemsNormalizedTracing || this.props.items,
        el: ReactDOM.findDOMNode(this),
      });
      logCustomTraceEvent({ name: 'UserNavRegisterGridItemsPositionedTraceEvent' });
      stopTiming(PCONST.thrift.TracingClientTypes.PWTAction.SEARCH_FEED_RENDER);
      stopTiming(PCONST.thrift.TracingClientTypes.PWTAction.HOME_FEED_RENDER);
      // Stop board render timing (small screens not requiring more grid item fetching)
      stopTiming(PCONST.thrift.TracingClientTypes.PWTAction.BOARD_PAGE_LOAD);
    }

    if (saveRoutePins) {
      this.updateRoutePins();
    }
  }

  componentDidUpdate(prevProps: Props) {
    const { saveRoutePins, items, viewParameterType } = this.props;

    if (this.isRecordingLoadItemMetrics && !this.props.isFetching) {
      this.isRecordingLoadItemMetrics = false;
      stopTiming(PCONST.thrift.TracingClientTypes.PWTAction.GRID_BOTTOM_SPINNER, {
        viewParameter: viewParameterType,
      });
      logCustomTraceEvent({ name: 'GridBottomSpinnerStoppedTraceEvent' });
    }

    if (!this.props.isFetching) {
      logCustomTraceEvent({
        name: 'UserNavRegisterGridImagesTraceEvent',
        viewportFilled: this.checkViewportFilled(),
        gridData: this.props.itemsNormalizedTracing || this.props.items,
        el: ReactDOM.findDOMNode(this),
      });
      logCustomTraceEvent({ name: 'UserNavRegisterGridItemsPositionedTraceEvent' });
      stopTiming(PCONST.thrift.TracingClientTypes.PWTAction.SEARCH_FEED_RENDER);
      stopTiming(PCONST.thrift.TracingClientTypes.PWTAction.HOME_FEED_RENDER);
      // Stop board render timing (large screens that required more grid item fetching)
      stopTiming(PCONST.thrift.TracingClientTypes.PWTAction.BOARD_PAGE_LOAD);
    }

    if (this.checkViewportFilled()) {
      logCustomTraceEvent({
        name: 'PageLoadRegisterGridImagesTraceEvent',
        gridData: this.props.itemsNormalizedTracing || this.props.items,
        el: ReactDOM.findDOMNode(this),
        viewportFilled: true,
      });
    }

    if (saveRoutePins && items !== prevProps.items) {
      this.updateRoutePins();
    }
  }

  gridVisibleHeight: number;
  isRecordingLoadItemMetrics = false;

  updateRoutePins() {
    const { currentHistoryEntry, setRoutePins, items } = this.props;

    if (currentHistoryEntry && items) {
      const pinIds = getPinIdsFromItems(items);
      setRoutePins(pinIds, currentHistoryEntry.location);
    }
  }

  checkViewportFilled(): boolean {
    const { cacheKey, useMasonry9 } = this.props;

    const measurementCache = getMeasurementCache(cacheKey, useMasonry9);
    if (measurementCache && this.gridVisibleHeight) {
      if (useMasonry9) {
        if (measurementCache.layout && measurementCache.layout.contentFilledSize) {
          const contentFilledSize = measurementCache.layout.contentFilledSize;
          return contentFilledSize.height > this.gridVisibleHeight;
        }
      } else if (measurementCache.columnHeights) {
        // Check that the shortest column is longer than the grid height
        return Math.min(...measurementCache.columnHeights) > this.gridVisibleHeight;
      }
    }

    return false;
  }

  loadItems = () => {
    if (!this.props.isFetching) {
      startTiming(PCONST.thrift.TracingClientTypes.PWTAction.GRID_BOTTOM_SPINNER);
      logCustomTraceEvent({ name: 'GridBottomSpinnerStartedTraceEvent' });
      this.isRecordingLoadItemMetrics = true;
      this.props.loadItems();
    }
  };

  renderItem = (props: Object) => {
    const { comp: Component } = this.props;
    return (
      <ErrorBoundary name="MasonryPerfTimingItem">
        <Component {...props} />
      </ErrorBoundary>
    );
  };

  render() {
    const {
      cacheKey,
      columnWidth,
      fetchBoundsOffset,
      flexible,
      gutterWidth,
      isGridCentered,
      items,
      masonryRef,
      minCols,
      paddingStyle,
      scrollContainer,
      serverRender,
      useMasonry9,
      virtualize,
      widthOffset,
    } = this.props;

    return (
      <ExperimentalMasonry
        columnWidth={columnWidth}
        comp={this.renderItem}
        fetchBoundsOffset={fetchBoundsOffset}
        flexible={flexible}
        gutterWidth={gutterWidth}
        items={items}
        loadItems={this.loadItems}
        masonryRef={masonryRef}
        measurementStore={getMeasurementCache(cacheKey, useMasonry9)}
        paddingStyle={paddingStyle}
        scrollContainer={scrollContainer || getWindow}
        serverRender={serverRender}
        useMasonry9={useMasonry9}
        virtualize={virtualize}
        widthOffset={widthOffset}
        isGridCentered={isGridCentered}
        minCols={minCols}
      />
    );
  }
}

function MasonryPerfTimingWithMasonry9Experiment(props: Props) {
  const requestContext = useRequestContext();
  const useMasonry9 =
    props.useMasonry9 === undefined
      ? new SingleTreatmentExp(requestContext, 'web_masonry9').enabled()
      : props.useMasonry9;
  const offset = props.fetchBoundsOffset || (useMasonry9 ? 100 : 0);
  return <MasonryPerfTiming {...props} useMasonry9={useMasonry9} fetchBoundsOffset={offset} />;
}

export const mapStateToProps = (
  { pins, history }: ReduxState,
  { mapPinUrlsToItems, items }: OwnProps,
): ReduxStateProps => ({
  currentHistoryEntry: history.current,
  itemsNormalizedTracing: mapPinUrlsToItems ? mapPinUrlsToItems(pins, items) : null,
});

export const mapDispatchToProps = (dispatch: Dispatch<Action>): ReduxDispatchProps => ({
  setRoutePins: (pinIds: Array<string>, location: HistoryLocation) =>
    dispatch(setRoutePinsAction(pinIds, location)),
});

export default connect<ConnectedProps, OwnProps, _, _, _, _>(
  mapStateToProps,
  mapDispatchToProps,
)(MasonryPerfTimingWithMasonry9Experiment);
